const LOCAL_API_ORIGIN = "http://localhost:8090";

const getBrowserLocation = () => {
  if (typeof window === "undefined") return null;
  return window.location;
};

const isLocalHost = (hostname = "") =>
  ["localhost", "127.0.0.1", "::1"].includes(String(hostname).trim().toLowerCase());

const resolveApiOrigin = () => {
  const envOrigin = String(process.env.REACT_APP_API_ORIGIN || "").trim().replace(/\/+$/, "");
  const location = getBrowserLocation();

  if (envOrigin) {
    return envOrigin;
  }

  if (location && !isLocalHost(location.hostname)) {
    if (String(location.port || "") === "3000") {
      return `${location.protocol}//${location.hostname}:8090`;
    }
    return "";
  }

  return location && isLocalHost(location.hostname) ? LOCAL_API_ORIGIN : "";
};

const API_ORIGIN = resolveApiOrigin();
const DEFAULT_ASSET_BASE_URL = API_ORIGIN ? `${API_ORIGIN}/api` : "/api";
const ASSET_BASE_URL = (process.env.REACT_APP_ASSET_BASE_URL || DEFAULT_ASSET_BASE_URL)
  .replace(/\/+$/, "")
  .replace(/\/api\/api$/i, "/api");

export const API_BASE_URL = API_ORIGIN ? `${API_ORIGIN}/api` : "/api";
const ABSOLUTE_ASSET_PATTERN = /^(?:[a-z][a-z\d+\-.]*:)?\/\//i;

export const buildApiUrl = (path = "") => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
};

export const buildAssetUrl = (path = "") => {
  if (!path) return ASSET_BASE_URL;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (/^\/api\/uploads(\/|$)/i.test(normalizedPath)) {
    return `${API_ORIGIN}${normalizedPath}`;
  }

  if (/^\/uploads(\/|$)/i.test(normalizedPath)) {
    return `${ASSET_BASE_URL}${normalizedPath}`;
  }

  return `${API_ORIGIN}${normalizedPath}`;
};

export const resolveAssetUrl = (assetPath = "", fallbackDirectory = "") => {
  const rawValue = String(assetPath || "")
    .trim()
    .replace(/\\/g, "/");
  if (!rawValue) return "";

  if (
    rawValue.startsWith("blob:") ||
    rawValue.startsWith("data:") ||
    ABSOLUTE_ASSET_PATTERN.test(rawValue)
  ) {
    return rawValue;
  }

  if (rawValue.startsWith("/")) {
    return buildAssetUrl(rawValue);
  }

  if (rawValue.startsWith("api/uploads/")) {
    return buildAssetUrl(`/${rawValue}`);
  }

  if (rawValue.startsWith("uploads/")) {
    return buildAssetUrl(`/${rawValue}`);
  }

  if (fallbackDirectory) {
    const normalizedDirectory = fallbackDirectory.replace(/^\/+|\/+$/g, "");
    return buildAssetUrl(`/${normalizedDirectory}/${rawValue}`);
  }

  return buildAssetUrl(`/${rawValue}`);
};

export const resolveAbsoluteAssetUrl = (assetPath = "", fallbackDirectory = "") => {
  const resolvedUrl = resolveAssetUrl(assetPath, fallbackDirectory);
  if (!resolvedUrl) {
    return "";
  }

  if (
    ABSOLUTE_ASSET_PATTERN.test(resolvedUrl) ||
    resolvedUrl.startsWith("blob:") ||
    resolvedUrl.startsWith("data:")
  ) {
    return resolvedUrl;
  }

  const location = getBrowserLocation();
  if (!location?.origin) {
    return resolvedUrl;
  }

  try {
    const normalizedResolvedUrl = String(resolvedUrl).replace(/\\/g, "/");
    return new URL(normalizedResolvedUrl, location.origin).toString();
  } catch {
    return resolvedUrl;
  }
};

export default API_ORIGIN;